import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SEO } from '@/components/SEO';
import { FileQuestion, Home, ArrowLeft, Sparkles } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

export default function NotFound() {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-[calc(100vh-12rem)] flex items-center justify-center bg-gradient-to-br from-indigo-50/50 via-white to-emerald-50/50 px-4 py-16">
      <SEO
        title="404 - Page Not Found | QuickFreeInvoice"
        description="The page you are looking for does not exist or has been moved."
        noindex={true}
      />
      
      <div className="w-full max-w-lg text-center space-y-8">
        {/* Floating 404 Icon */}
        <div className="relative inline-flex items-center justify-center">
          <div className="absolute -inset-4 bg-indigo-500/10 rounded-full blur-xl animate-pulse"></div>
          <div className="relative bg-white border border-indigo-100 shadow-xl p-6 rounded-3xl text-indigo-600">
            <FileQuestion className="h-16 w-16 stroke-[1.5]" />
          </div>
        </div>

        {/* Text Details */}
        <div className="space-y-3">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200 uppercase tracking-wider">
            Error 404
          </span>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
            Page Not Found
          </h1>
          <p className="text-slate-500 text-base md:text-lg max-w-md mx-auto leading-relaxed">
            Sorry, the page you're looking for doesn't exist, was removed, or had its name changed.
          </p>
        </div>

        {/* Quick Action Buttons */}
        <Card className="border border-slate-200/80 shadow-sm bg-white/80 backdrop-blur-sm">
          <CardContent className="p-6 space-y-4">
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                onClick={() => navigate('/')}
                className="bg-primary hover:bg-primary/90 text-white gap-2 font-semibold shadow-md shadow-primary/20"
              >
                <Home className="h-4 w-4" />
                Back to Home
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate(user ? '/dashboard' : '/try-it-out')}
                className="gap-2 font-medium border-slate-300 hover:bg-slate-50"
              >
                <Sparkles className="h-4 w-4 text-indigo-600" />
                {user ? 'Go to Dashboard' : 'Try Free Generator'}
              </Button>
            </div>

            {/* Navigation links list */}
            <div className="pt-4 border-t border-slate-100 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs font-medium text-slate-500">
              <Link to="/pricing" className="hover:text-primary transition-colors">
                Pricing Plans
              </Link>
              <span className="text-slate-300">•</span>
              <Link to="/blog" className="hover:text-primary transition-colors">
                Invoicing Blog
              </Link>
              <span className="text-slate-300">•</span>
              <Link to="/try-it-out" className="hover:text-primary transition-colors">
                Instant Playground
              </Link>
              <span className="text-slate-300">•</span>
              <Link to="/security" className="hover:text-primary transition-colors">
                Security & Privacy
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Back Link */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Go back to previous page
        </button>
      </div>
    </div>
  );
}
